var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { ChangeEventListener, FocusEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { getValueFromInternalValue, normalizeUTCShortTime } from './time-field-utils.js';
class TimeFieldAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onBlur = new FocusEventListener();
        /** @event */
        this.onChange = new ChangeEventListener();
        /** @event */
        this.onFocus = new FocusEventListener();
    }
    /**
     * Returns the expression of the apply button.
     */
    getApplyExpression() {
        return this.state.applyExpression;
    }
    /**
     * Sets the expression of the apply button.
     *
     * @param applyExpression - The expression of the apply button.
     */
    setApplyExpression(applyExpression) {
        this.setState({ applyExpression });
    }
    /**
     * Returns the field data.
     */
    getData() {
        return this.state.data;
    }
    /**
     * Sets the field data.
     *
     * @param data - The field data.
     */
    setData(data) {
        this.setState({ data });
    }
    /**
     * Returns if the date field is enabled.
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the date field is enabled.
     *
     * @param enabled - If the date field is enabled.
     */
    setEnabled(enabled) {
        this.setState({ enabled });
    }
    /**
     * Returns the expression of the "from" text.
     */
    getFromExpression() {
        return this.state.fromExpression;
    }
    /**
     * Sets the expression of the "from" text.
     *
     * @param fromExpression - The expression of the "from" text.
     */
    setFromExpression(fromExpression) {
        this.setState({ fromExpression });
    }
    /**
     * Returns a boolean indicating if the interval selection is enabled.
     */
    getInterval() {
        return this.state.interval;
    }
    /**
     * Sets if the field should allow the selection of an interval.
     *
     * @param interval - If the field should allow the selection of an interval.
     */
    setInterval(interval) {
        this.setState({ interval });
    }
    /**
     * Returns the expression for when the field has no value.
     */
    getNoValueExpression() {
        return this.state.noValueExpression;
    }
    /**
     * Sets the expression for when the field has no value.
     *
     * @param fromExpression - The expression for when the field has no value.
     */
    setNoValueExpression(noValueExpression) {
        this.setState({ noValueExpression });
    }
    /**
     * Returns the expression of the "now" text.
     */
    getNowExpression() {
        return this.state.nowExpression;
    }
    /**
     * Sets the expression of the "now" text.
     *
     * @param nowExpression - The expression of the "now" text.
     */
    setNowExpression(nowExpression) {
        this.setState({ nowExpression });
    }
    /**
     * Returns the expression of the "ok" text.
     */
    getOkExpression() {
        return this.state.okExpression;
    }
    /**
    * Sets the expression of the "ok" text.
    *
    * @param okExpression - The expression of the "ok" text.
    */
    setOkExpression(okExpression) {
        this.setState({ okExpression });
    }
    /**
     * Returns a boolean indicating if the field is read only.
     */
    isReadOnly() {
        return this.state.readOnly;
    }
    /**
     * Sets if the field should be read only.
     *
     * @param readOnly - If the field should be read only.
     */
    setReadOnly(readOnly) {
        this.setState({ readOnly });
    }
    /**
     * Returns the selection mode of the field.
     */
    getSelectionMode() {
        return this.state.selectionMode;
    }
    /**
     * Sets the selection mode of the field.
     * @param selectionMode - The new selection mode.
     */
    setSelectionMode(selectionMode) {
        this.setState({ selectionMode });
    }
    /**
     * Returns the expression of the "until" text.
     */
    getUntilExpression() {
        return this.state.untilExpression;
    }
    /**
    * Sets the expression of the "until" text.
    *
    * @param untilExpression - The expression of the "until" text.
    */
    setUntilExpression(untilExpression) {
        this.setState({ untilExpression });
    }
    /**
     * Returns the field value.
     */
    getValue() {
        const { internalValue } = this.state;
        return getValueFromInternalValue(internalValue);
    }
    /**
     * Sets the field value.
     * @param value - The value.
     */
    setValue(value) {
        const newValue = Array.isArray(value)
            ? value.map(normalizeUTCShortTime)
            : normalizeUTCShortTime(value);
        return this.setState({ value: newValue });
    }
    /**
     * Returns the configured sensitiveness for the field.
     */
    getSensitiveData() {
        return this.state.sensitiveData;
    }
    /**
     * Sets if the data of the field should be hidden when in read only mode.
     *
     * @param sensitiveData - How sensitive the data is.
     */
    setSensitiveData(sensitiveData) {
        this.setState({ sensitiveData });
    }
    /**
     * Returns if the component clear action should be shown.
     */
    getShowClearAction() {
        return this.state.showClearAction;
    }
    /**
     * Sets if the component clear action should be shown.
     *
     * @param showClearAction - The updated showClearAction value.
     */
    setShowClearAction(showClearAction) {
        this.setState({ showClearAction });
    }
    /**
     * Returns a boolean indicating if the field value is being struckthrough.
     */
    isStrikethrough() {
        return Boolean(this.state.strikethrough);
    }
    /**
     * Sets if the field value should be struckthrough.
     *
     * @param strikethrough - If the field value should be struckthrough.
     */
    setStrikethrough(strikethrough) {
        this.setState({ strikethrough });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TimeFieldAPI.prototype, "onBlur", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TimeFieldAPI.prototype, "onChange", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TimeFieldAPI.prototype, "onFocus", void 0);
export default TimeFieldAPI;
//# sourceMappingURL=time-field-api.js.map